import React, { useEffect, useMemo, useState } from 'react';
import {
  SERVICE_BILLING_CADENCE_OPTIONS,
  SERVICE_CONTRACT_MODEL_OPTIONS,
  SERVICE_DRIVER_ASSIGNMENT_OPTIONS,
  SERVICE_MARGIN_PRESET_OPTIONS,
  SERVICE_PRICING_RULE_OPTIONS,
  SERVICE_TRANSPORT_ASSIGNMENT_OPTIONS,
  SERVICE_WEEKDAY_OPTIONS,
  buildClientDisplayName,
  inferMarginPreset,
  resolveMarginPresetPercent,
} from '../serviceOptions';
import {
  resolveBillingCadenceLabel,
  resolveContractBillingCadence,
  resolveWeekdayLabel,
} from '../serviceBilling';

const baseFieldClassName =
  'w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-cyan-300 focus:ring-4 focus:ring-cyan-100';

const EMPTY_FORM = {
  name: '',
  status: 'active',
  commercialModel: 'trip_rate',
  billingCadence: 'per_trip',
  billingDayOfWeek: 'friday',
  billingDayOfMonth: '1',
  serviceDaysOfWeek: [],
  serviceTimeWindowStart: '',
  serviceTimeWindowEnd: '',
  serviceStartDate: '',
  serviceEndDate: '',
  transportAssignmentMode: 'free',
  fixedTransportId: '',
  suggestedTransportId: '',
  driverAssignmentMode: 'free',
  fixedDriverId: '',
  suggestedDriverId: '',
  pricingRule: 'manual_total',
  billingContactName: '',
  billingPhone: '',
  billingEmail: '',
  autoReminderEnabled: false,
  reminderDaysBefore: '3',
  monthlyFee: '',
  agreedPricePerTrip: '',
  fixedMarginAmount: '',
  marginPreset: 'normal',
  marginPercent: '40',
  notes: '',
};

const Icon = ({ path, className = 'w-5 h-5' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d={path} />
  </svg>
);

const InputBlock = ({ label, children, helper = '' }) => (
  <label className="block space-y-2">
    <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
    {children}
    {helper ? <span className="block text-xs text-slate-400">{helper}</span> : null}
  </label>
);

const buildDefaultAgreementName = (commercialModel = 'trip_rate', client = null) => {
  const clientName = buildClientDisplayName(client);
  const labels = {
    trip_rate: 'Por viaje',
    monthly_fixed: 'Abono fijo',
    monthly_variable: 'Mensual variable',
  };
  return `${labels[commercialModel] || 'Acuerdo'} - ${clientName}`;
};

export default function ServiceContractEditorModal({
  open,
  client = null,
  contract = null,
  transports = [],
  drivers = [],
  saving = false,
  onClose,
  onSubmit,
}) {
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (!open) {
      setForm(EMPTY_FORM);
      return;
    }

    setForm({
      name: contract?.name || '',
      status: contract?.status || 'active',
      commercialModel: contract?.commercialModel || 'trip_rate',
      billingCadence: contract?.billingCadence || resolveContractBillingCadence(contract),
      billingDayOfWeek: contract?.billingDayOfWeek || 'friday',
      billingDayOfMonth: String(contract?.billingDayOfMonth ?? '1'),
      serviceDaysOfWeek: Array.isArray(contract?.serviceDaysOfWeek) ? contract.serviceDaysOfWeek : [],
      serviceTimeWindowStart: contract?.serviceTimeWindowStart || '',
      serviceTimeWindowEnd: contract?.serviceTimeWindowEnd || '',
      serviceStartDate: contract?.serviceStartDate || '',
      serviceEndDate: contract?.serviceEndDate || '',
      transportAssignmentMode: contract?.transportAssignmentMode || 'free',
      fixedTransportId: contract?.fixedTransportId || '',
      suggestedTransportId: contract?.suggestedTransportId || '',
      driverAssignmentMode: contract?.driverAssignmentMode || 'free',
      fixedDriverId: contract?.fixedDriverId || '',
      suggestedDriverId: contract?.suggestedDriverId || '',
      pricingRule: contract?.pricingRule || 'manual_total',
      billingContactName: contract?.billingContactName || '',
      billingPhone: contract?.billingPhone || '',
      billingEmail: contract?.billingEmail || '',
      autoReminderEnabled: Boolean(contract?.autoReminderEnabled),
      reminderDaysBefore: String(contract?.reminderDaysBefore ?? '3'),
      monthlyFee: contract?.monthlyFee ?? '',
      agreedPricePerTrip: contract?.agreedPricePerTrip ?? '',
      fixedMarginAmount: contract?.fixedMarginAmount ?? '',
      marginPreset: contract?.marginPreset || inferMarginPreset(contract?.marginPercent),
      marginPercent: contract?.marginPercent ?? resolveMarginPresetPercent(contract?.marginPreset || inferMarginPreset(contract?.marginPercent)),
      notes: contract?.notes || '',
    });
  }, [open, contract]);

  useEffect(() => {
    if (!open || contract) return;
    setForm((current) => {
      if (String(current.name || '').trim()) return current;
      return {
        ...current,
        name: buildDefaultAgreementName(current.commercialModel, client),
      };
    });
  }, [open, contract, client, form.commercialModel]);

  const commercialModelMeta = useMemo(
    () => SERVICE_CONTRACT_MODEL_OPTIONS.find((option) => option.value === form.commercialModel) || null,
    [form.commercialModel]
  );

  const pricingRuleMeta = useMemo(
    () => SERVICE_PRICING_RULE_OPTIONS.find((option) => option.value === form.pricingRule) || null,
    [form.pricingRule]
  );

  const billingCadenceMeta = useMemo(
    () => SERVICE_BILLING_CADENCE_OPTIONS.find((option) => option.value === form.billingCadence) || SERVICE_BILLING_CADENCE_OPTIONS[0],
    [form.billingCadence]
  );

  const transportAssignmentMeta = useMemo(
    () => SERVICE_TRANSPORT_ASSIGNMENT_OPTIONS.find((option) => option.value === form.transportAssignmentMode) || SERVICE_TRANSPORT_ASSIGNMENT_OPTIONS[0],
    [form.transportAssignmentMode]
  );

  const driverAssignmentMeta = useMemo(
    () => SERVICE_DRIVER_ASSIGNMENT_OPTIONS.find((option) => option.value === form.driverAssignmentMode) || SERVICE_DRIVER_ASSIGNMENT_OPTIONS[0],
    [form.driverAssignmentMode]
  );

  const marginPresetMeta = useMemo(
    () => SERVICE_MARGIN_PRESET_OPTIONS.find((option) => option.value === form.marginPreset) || SERVICE_MARGIN_PRESET_OPTIONS[1],
    [form.marginPreset]
  );

  if (!open || !client) return null;

  const handleChange = (field) => (event) => {
    const target = event?.target;
    const value = target?.type === 'checkbox' ? Boolean(target.checked) : target?.value ?? '';
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleMarginPresetChange = (event) => {
    const nextPreset = event?.target?.value || 'normal';
    setForm((current) => ({
      ...current,
      marginPreset: nextPreset,
      marginPercent:
        nextPreset === 'manual'
          ? current.marginPercent
          : String(resolveMarginPresetPercent(nextPreset)),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;
    await onSubmit?.(form, client, contract);
  };

  const toggleServiceDay = (weekday) => {
    setForm((current) => {
      const currentDays = Array.isArray(current.serviceDaysOfWeek) ? current.serviceDaysOfWeek : [];
      const nextDays = currentDays.includes(weekday)
        ? currentDays.filter((day) => day !== weekday)
        : [...currentDays, weekday];
      return { ...current, serviceDaysOfWeek: nextDays };
    });
  };

  return (
    <div className="fixed inset-0 z-[1850] flex items-center justify-center bg-slate-950/60 px-4 py-4 backdrop-blur-sm">
      <div className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-[32px] border border-white/70 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.28)]">
        <div className="border-b border-slate-200 bg-[linear-gradient(135deg,#0f172a_0%,#155e75_56%,#cffafe_100%)] px-6 py-5 text-white">
          <div className="flex items-start justify-between gap-4">
            <div className="max-w-3xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-cyan-100">Contrato comercial</p>
              <h2 className="mt-2 text-2xl font-black tracking-tight">{buildClientDisplayName(client)}</h2>
              <p className="mt-2 text-sm leading-6 text-cyan-50/90">
                Definimos cómo cobra la empresa este cliente sin tocar la liquidación del chofer ni el costo operativo base.
              </p>
            </div>
            <button
              type="button"
              onClick={saving ? undefined : onClose}
              className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/20 bg-white/10 text-white transition hover:bg-white/16 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={saving}
            >
              <Icon path="M6 18L18 6M6 6l12 12" className="h-5 w-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              <InputBlock label="Nombre comercial" helper="Ejemplo: Logística mensual Cuenca o tarifa especial por viaje.">
                <input value={form.name} onChange={handleChange('name')} className={baseFieldClassName} placeholder="Nombre del acuerdo" required />
              </InputBlock>
              <InputBlock label="Estado">
                <select value={form.status} onChange={handleChange('status')} className={baseFieldClassName}>
                  <option value="active">Activo</option>
                  <option value="draft">Borrador</option>
                  <option value="paused">Pausado</option>
                  <option value="closed">Cerrado</option>
                </select>
              </InputBlock>
              <InputBlock label="Modo comercial">
                <select value={form.commercialModel} onChange={handleChange('commercialModel')} className={baseFieldClassName}>
                  {SERVICE_CONTRACT_MODEL_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </InputBlock>
              <InputBlock label="Modo de cobro">
                <select value={form.billingCadence} onChange={handleChange('billingCadence')} className={baseFieldClassName}>
                  {SERVICE_BILLING_CADENCE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </InputBlock>
            </div>

            <div className="rounded-[24px] border border-blue-200 bg-blue-50/70 px-5 py-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-700">Cobro acumulativo</p>
              <p className="mt-2 text-base font-black tracking-tight text-blue-950">
                {billingCadenceMeta?.label || resolveBillingCadenceLabel(form.billingCadence)}
              </p>
              <p className="mt-2 text-sm leading-6 text-blue-900/80">
                {billingCadenceMeta?.description}
              </p>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {form.billingCadence === 'weekly' ? (
                  <InputBlock label="Día de cobro">
                    <select value={form.billingDayOfWeek} onChange={handleChange('billingDayOfWeek')} className={baseFieldClassName}>
                      {SERVICE_WEEKDAY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </InputBlock>
                ) : null}
                {form.billingCadence === 'monthly' ? (
                  <InputBlock label="Día del mes">
                    <input type="number" min="1" max="31" value={form.billingDayOfMonth} onChange={handleChange('billingDayOfMonth')} className={baseFieldClassName} placeholder="1" />
                  </InputBlock>
                ) : null}
                <InputBlock label="Calendario de servicio">
                  <input
                    type="text"
                    readOnly
                    value={
                      form.serviceDaysOfWeek.length > 0
                        ? form.serviceDaysOfWeek.map(resolveWeekdayLabel).join(', ')
                        : 'Libre / a pedido'
                    }
                    className={`${baseFieldClassName} bg-white/90 text-slate-600`}
                  />
                </InputBlock>
              </div>
            </div>

            <div className="rounded-[24px] border border-emerald-200 bg-emerald-50/70 px-5 py-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-700">Cobro y contacto</p>
              <p className="mt-2 text-sm leading-6 text-emerald-900/80">
                Estos datos nos dejan lista la ficha para avisos institucionales y seguimiento del cobro mensual.
              </p>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <InputBlock label="Contacto de cobro">
                  <input value={form.billingContactName} onChange={handleChange('billingContactName')} className={baseFieldClassName} placeholder="Nombre o área administrativa" />
                </InputBlock>
                <InputBlock label="Teléfono">
                  <input value={form.billingPhone} onChange={handleChange('billingPhone')} className={baseFieldClassName} placeholder="WhatsApp o teléfono" />
                </InputBlock>
                <InputBlock label="Correo electrónico">
                  <input type="email" value={form.billingEmail} onChange={handleChange('billingEmail')} className={baseFieldClassName} placeholder="facturacion@cliente.com" />
                </InputBlock>
                <InputBlock label="Aviso previo (días)">
                  <input type="number" min="0" step="1" value={form.reminderDaysBefore} onChange={handleChange('reminderDaysBefore')} className={baseFieldClassName} placeholder="3" />
                </InputBlock>
              </div>

              <label className="mt-4 inline-flex items-center gap-3 rounded-2xl border border-emerald-200 bg-white px-4 py-3 text-sm font-semibold text-emerald-900 shadow-sm">
                <input
                  type="checkbox"
                  checked={Boolean(form.autoReminderEnabled)}
                  onChange={handleChange('autoReminderEnabled')}
                  className="h-4 w-4 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-200"
                />
                Activar recordatorio previo al cobro
              </label>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-slate-50/80 px-5 py-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Calendario del servicio</p>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    Definimos qué días corre el acuerdo, su franja horaria y si tiene fecha de inicio o fin.
                  </p>
                </div>
                <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-600">
                  {form.serviceDaysOfWeek.length > 0 ? `${form.serviceDaysOfWeek.length} días configurados` : 'A pedido'}
                </span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {SERVICE_WEEKDAY_OPTIONS.map((option) => {
                  const active = form.serviceDaysOfWeek.includes(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => toggleServiceDay(option.value)}
                      className={`rounded-full border px-3 py-2 text-xs font-semibold uppercase tracking-[0.16em] transition ${
                        active
                          ? 'border-cyan-300 bg-cyan-600 text-white shadow-sm'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-cyan-200 hover:text-cyan-700'
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <InputBlock label="Hora desde">
                  <input type="time" value={form.serviceTimeWindowStart} onChange={handleChange('serviceTimeWindowStart')} className={baseFieldClassName} />
                </InputBlock>
                <InputBlock label="Hora hasta">
                  <input type="time" value={form.serviceTimeWindowEnd} onChange={handleChange('serviceTimeWindowEnd')} className={baseFieldClassName} />
                </InputBlock>
                <InputBlock label="Inicio del acuerdo">
                  <input type="date" value={form.serviceStartDate} onChange={handleChange('serviceStartDate')} className={baseFieldClassName} />
                </InputBlock>
                <InputBlock label="Fin del acuerdo">
                  <input type="date" value={form.serviceEndDate} onChange={handleChange('serviceEndDate')} className={baseFieldClassName} />
                </InputBlock>
              </div>
            </div>

            <div className="rounded-[24px] border border-cyan-200 bg-cyan-50/70 px-5 py-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-700">Asignación operativa</p>
              <p className="mt-2 text-sm leading-6 text-cyan-900/80">
                Podemos dejar la unidad libre, sugerir una o fijarla para que el viaje programado nazca preasignado.
              </p>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                <InputBlock label="Modo de asignación">
                  <select value={form.transportAssignmentMode} onChange={handleChange('transportAssignmentMode')} className={baseFieldClassName}>
                    {SERVICE_TRANSPORT_ASSIGNMENT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </InputBlock>
                {form.transportAssignmentMode === 'fixed' ? (
                  <InputBlock label="Transporte fijo">
                    <select value={form.fixedTransportId} onChange={handleChange('fixedTransportId')} className={baseFieldClassName}>
                      <option value="">Elegir unidad</option>
                      {transports.map((transport) => (
                        <option key={transport.id} value={transport.id}>
                          {transport.name || transport.displayName || transport.plate || transport.id}
                        </option>
                      ))}
                    </select>
                  </InputBlock>
                ) : null}
                {form.transportAssignmentMode === 'suggested' ? (
                  <InputBlock label="Transporte sugerido">
                    <select value={form.suggestedTransportId} onChange={handleChange('suggestedTransportId')} className={baseFieldClassName}>
                      <option value="">Elegir sugerencia</option>
                      {transports.map((transport) => (
                        <option key={transport.id} value={transport.id}>
                          {transport.name || transport.displayName || transport.plate || transport.id}
                        </option>
                      ))}
                    </select>
                  </InputBlock>
                ) : null}
                <div className="rounded-[22px] border border-cyan-200 bg-white/90 px-4 py-3 shadow-sm">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-700">Lectura rápida</p>
                  <p className="mt-1 text-sm font-bold text-slate-900">{transportAssignmentMeta?.label || 'Libre'}</p>
                  <p className="mt-1 text-xs text-slate-500">{transportAssignmentMeta?.description}</p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                <InputBlock label="Chofer">
                  <select value={form.driverAssignmentMode} onChange={handleChange('driverAssignmentMode')} className={baseFieldClassName}>
                    {SERVICE_DRIVER_ASSIGNMENT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </InputBlock>
                {form.driverAssignmentMode === 'fixed' ? (
                  <InputBlock label="Chofer fijo">
                    <select value={form.fixedDriverId} onChange={handleChange('fixedDriverId')} className={baseFieldClassName}>
                      <option value="">Elegir chofer</option>
                      {drivers.map((driver) => (
                        <option key={driver.id} value={driver.id}>
                          {driver.firstName || driver.name || 'Chofer'} {driver.lastName || ''}
                        </option>
                      ))}
                    </select>
                  </InputBlock>
                ) : null}
                {form.driverAssignmentMode === 'suggested' ? (
                  <InputBlock label="Chofer sugerido">
                    <select value={form.suggestedDriverId} onChange={handleChange('suggestedDriverId')} className={baseFieldClassName}>
                      <option value="">Elegir sugerencia</option>
                      {drivers.map((driver) => (
                        <option key={driver.id} value={driver.id}>
                          {driver.firstName || driver.name || 'Chofer'} {driver.lastName || ''}
                        </option>
                      ))}
                    </select>
                  </InputBlock>
                ) : null}
                <div className="rounded-[22px] border border-cyan-200 bg-white/90 px-4 py-3 shadow-sm">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-700">Chofer asignado</p>
                  <p className="mt-1 text-sm font-bold text-slate-900">{driverAssignmentMeta?.label || 'Libre'}</p>
                  <p className="mt-1 text-xs text-slate-500">{driverAssignmentMeta?.description}</p>
                </div>
              </div>
            </div>

            <div className="rounded-[24px] border border-cyan-200 bg-cyan-50/70 px-5 py-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-700">Modo elegido</p>
              <p className="mt-2 text-base font-black tracking-tight text-cyan-950">
                {commercialModelMeta?.label || 'Acuerdo comercial'}
              </p>
              <p className="mt-2 text-sm leading-6 text-cyan-900/80">{commercialModelMeta?.description}</p>
            </div>

            {form.commercialModel === 'monthly_fixed' ? (
              <InputBlock label="Valor mensual pactado">
                <input type="number" min="0" step="0.01" value={form.monthlyFee} onChange={handleChange('monthlyFee')} className={baseFieldClassName} placeholder="0" required />
              </InputBlock>
            ) : null}

            {form.commercialModel !== 'monthly_fixed' ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                <InputBlock label="Regla de precio">
                  <select value={form.pricingRule} onChange={handleChange('pricingRule')} className={baseFieldClassName}>
                    {SERVICE_PRICING_RULE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </InputBlock>

                {form.pricingRule === 'manual_total' ? (
                  <InputBlock label="Precio de referencia por viaje">
                    <input type="number" min="0" step="0.01" value={form.agreedPricePerTrip} onChange={handleChange('agreedPricePerTrip')} className={baseFieldClassName} placeholder="0" required />
                  </InputBlock>
                ) : null}

                {form.pricingRule === 'fixed_margin' ? (
                  <InputBlock label="Ganancia fija por viaje">
                    <input type="number" min="0" step="0.01" value={form.fixedMarginAmount} onChange={handleChange('fixedMarginAmount')} className={baseFieldClassName} placeholder="0" required />
                  </InputBlock>
                ) : null}

                {form.pricingRule === 'percentage_margin' ? (
                  <>
                    <InputBlock label="Nivel de precio">
                      <select value={form.marginPreset} onChange={handleMarginPresetChange} className={baseFieldClassName}>
                        {SERVICE_MARGIN_PRESET_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}{Number.isFinite(Number(option.percent)) ? ` · ${option.percent}%` : ''}
                          </option>
                        ))}
                      </select>
                    </InputBlock>

                    {form.marginPreset === 'manual' ? (
                      <InputBlock label="Porcentaje de ganancia">
                        <input type="number" min="0" step="0.01" value={form.marginPercent} onChange={handleChange('marginPercent')} className={baseFieldClassName} placeholder="40" required />
                      </InputBlock>
                    ) : (
                      <InputBlock label="Porcentaje aplicado">
                        <input
                          type="text"
                          value={`${resolveMarginPresetPercent(form.marginPreset)}%`}
                          readOnly
                          className={`${baseFieldClassName} bg-slate-50 text-slate-600`}
                        />
                      </InputBlock>
                    )}
                  </>
                ) : null}
              </div>
            ) : null}

            {form.commercialModel !== 'monthly_fixed' ? (
              <div className="rounded-[24px] border border-violet-200 bg-violet-50/70 px-5 py-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-violet-700">Regla de precio</p>
                <p className="mt-2 text-base font-black tracking-tight text-violet-950">
                  {pricingRuleMeta?.label || 'Precio comercial'}
                </p>
                <p className="mt-2 text-sm leading-6 text-violet-900/80">{pricingRuleMeta?.description}</p>
                {form.pricingRule === 'percentage_margin' ? (
                  <p className="mt-2 text-sm font-semibold text-violet-900/90">
                    {marginPresetMeta?.label || 'Normal'}{Number.isFinite(Number(marginPresetMeta?.percent)) ? ` · ${marginPresetMeta.percent}%` : ` · ${Number(form.marginPercent || 0) || 0}%`}
                  </p>
                ) : null}
              </div>
            ) : null}

            <InputBlock label="Observación interna" helper="Esto nos ayuda a recordar cómo se pactó el servicio o qué condiciones especiales tiene.">
              <textarea value={form.notes} onChange={handleChange('notes')} className={`${baseFieldClassName} min-h-[110px] resize-y`} placeholder="Ejemplo: cliente mensual con revisión cada 30 días, precio sujeto a peajes o acuerdo especial por urgencias." />
            </InputBlock>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 bg-white px-6 py-4">
            <button type="button" onClick={onClose} disabled={saving} className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60">
              Cancelar
            </button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-2xl border border-violet-300 bg-violet-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60">
              <Icon path={saving ? 'M12 6v6l4 2.5' : 'M12 4.5v15m7.5-7.5h-15'} className="h-4 w-4" />
              {saving ? 'Guardando acuerdo...' : 'Guardar acuerdo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
